import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRingCentralSafe } from '../../providers/RingCentralProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { useVonageSafe } from '../../providers/VonageProvider';
import { LoadingSpinner } from '../display/LoadingSpinner';
import { Button } from '../forms/Button';
import { OAuthHandlerRegistry } from './handlers/OAuthHandlerRegistry';
import { QboOAuthHandler } from './handlers/QboOAuthHandler';
import { RingCentralOAuthHandler } from './handlers/RingCentralOAuthHandler';
import { VonageOAuthHandler } from './handlers/VonageOAuthHandler';
import type { OAuthCallbackParams } from './oauth-callback.types';

OAuthHandlerRegistry.register(RingCentralOAuthHandler);
OAuthHandlerRegistry.register(QboOAuthHandler);
OAuthHandlerRegistry.register(VonageOAuthHandler);

type CallbackStatus = 'processing' | 'success' | 'error';

// OAuth authorization codes are single-use. This page can remount mid-flight (e.g. a
// background token refresh briefly swaps the app shell out to a loading screen), which
// resets the per-mount `processedRef` guard below. We track handled callbacks at module
// scope — keyed by the code+state read straight from the URL — so the handler runs
// exactly once per callback even across remounts within the same page session. This is
// why we no longer strip the params from the URL after reading them: the values stay put
// (our existing "storage" is the URL itself) and a remount can still recognise the
// in-flight callback and avoid re-exchanging a now-spent code.
const handledOAuthCallbacks = new Set<string>();

export function OAuthCallbackPage(): React.ReactElement {
	const { t } = useTranslation();
	const ringCentral = useRingCentralSafe();
	const vonage = useVonageSafe();
	const [status, setStatus] = useState<CallbackStatus>('processing');
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const processedRef = useRef(false);
	const navigate = useNavigate();

	useEffect(() => {
		if (processedRef.current) {
			return;
		}

		processedRef.current = true;

		const handleCallback = async (): Promise<void> => {
			const urlParams = new URLSearchParams(window.location.search);

			const params: OAuthCallbackParams = {
				code: urlParams.get('code'),
				state: urlParams.get('state'),
				error: urlParams.get('error'),
				errorDescription: urlParams.get('error_description'),
				realmId: urlParams.get('realmId'),
			};

			if (params.error != null) {
				setStatus('error');
				setErrorMessage(params.errorDescription ?? params.error);

				return;
			}

			if (params.code == null || params.state == null) {
				setStatus('error');
				setErrorMessage(
					t('Invalid OAuth callback. Missing required parameters.'),
				);

				return;
			}

			// Guard against running the handler twice for the same callback. The
			// per-mount `processedRef` covers effect re-runs, but a genuine remount
			// resets it; this module-scoped marker survives the remount so we never
			// re-exchange the single-use authorization code.
			const callbackKey = `${params.code}:${params.state}`;

			if (handledOAuthCallbacks.has(callbackKey)) {
				// Already handled by the original mount in this page session. Don't
				// re-run the handler — show success and let that original mount drive
				// the post-success navigate (its `navigate` dispatches to the still-
				// mounted router, so it fires even though that instance unmounted).
				setStatus('success');

				return;
			}

			handledOAuthCallbacks.add(callbackKey);

			const handler = OAuthHandlerRegistry.findHandler(params);

			if (handler == null) {
				setStatus('error');
				setErrorMessage(
					t('Unknown OAuth callback. Unable to determine integration.'),
				);

				return;
			}

			try {
				const result = await handler.handle(params);

				if (result.success) {
					setStatus('success');

					if (result.closePopup === true) {
						const opener = window.opener as Window | null;

						if (opener != null && result.postMessage != null) {
							opener.postMessage(result.postMessage, window.location.origin);
						}

						window.close();

						return;
					}

					// Refresh provider status based on which callback handler was used
					if (handler.name === 'ringcentral') {
						void ringCentral?.refreshStatus();
					} else if (handler.name === 'vonage') {
						void vonage?.refreshStatus();
					}

					if (result.redirectTo != null) {
						void navigate(result.redirectTo);
					}
				} else {
					// For user flows (closePopup=true), send error to opener and close
					if (result.closePopup === true) {
						const opener = window.opener as Window | null;

						if (opener != null && result.postMessage != null) {
							opener.postMessage(result.postMessage, window.location.origin);
						}

						window.close();

						return;
					}

					// For admin flows, show error UI
					setStatus('error');
					setErrorMessage(
						result.error ?? t('Failed to complete authentication'),
					);
				}
			} catch (err) {
				console.error('Error handling OAuth callback:', err);
				setStatus('error');
				setErrorMessage(
					t('An unexpected error occurred during authentication'),
				);
			}
		};

		void handleCallback();
	}, [navigate, ringCentral, vonage, t]);

	if (status === 'processing') {
		return (
			<div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
				<div className="text-center space-y-4">
					<LoadingSpinner size="lg" color="primary" />
					<p className="text-gray-600 dark:text-gray-400">
						{t('Processing authentication...')}
					</p>
				</div>
			</div>
		);
	}

	if (status === 'error') {
		return (
			<div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
				<div className="text-center space-y-4 max-w-md px-4">
					<div className="text-red-500 text-5xl">!</div>
					<h1 className="text-xl font-semibold text-gray-900 dark:text-white">
						{t('Authentication Failed')}
					</h1>
					<p className="text-gray-600 dark:text-gray-400">
						{t(errorMessage ?? 'An error occurred during authentication')}
					</p>
					<Button
						type="button"
						onClick={() => {
							void navigate('/settings/integrations');
						}}
						variant="primary"
						size="md"
					>
						{t('Return to Integrations')}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
			<div className="text-center space-y-4">
				<div className="text-green-500 text-5xl">✓</div>
				<p className="text-gray-600 dark:text-gray-400">
					{t('Authentication successful! Redirecting...')}
				</p>
			</div>
		</div>
	);
}
